import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { RouteRecordNormalized } from 'vue-router'
import { useConfigStore } from '@shared/stores/config'
import { AuthPermission, authPermissionEquals, PermissionLevel, AuthScope, useAuthStore } from '@shared/stores/auth'

// interfaces
import { ITopAppBarMenuItem } from '@shared/design-system/types/ITopAppBarMenuItem'
import { getSharedRouter } from '@shared/services/router'

interface Route {
  name: string;
  authScope: AuthPermission[];
  displayName: string;
  route: string;
  external: boolean;
  id: string;
}

export const useTopAppBarItemsStore = defineStore('TopAppBarItemsStore', () => {
  const authStore = useAuthStore()
  const router = getSharedRouter()
  const configStore = useConfigStore()

  const updatedVersion = ref<boolean>(false)
  const settingsMenuItem = ref<ITopAppBarMenuItem | undefined>(undefined)
  const userSettingsRoute = ref<string>()
  const interval = ref<NodeJS.Timeout | undefined>(undefined)

  const dynamicRoutes = ref<RouteRecordNormalized[]>(router.getRoutes())

  const ROUTES = computed<Route[]>(() => {
    const baseUrl = authStore.insightsUrl && authStore.insightsUrl.length > 0 ? authStore.insightsUrl : ''

    const routes: Route[] = []
    if (dynamicRoutes.value.some((route) => route.name === 'AnalyticsDashboard')) {
      routes.push({
        name: 'AnalyticsDashboard',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.AnalyticsDashboard }],
        displayName: 'Dashboard',
        route: 'AnalyticsDashboard',
        external: false,
        id: 'top_appbar_dashboard',
      })
    }
    if (dynamicRoutes.value.some((route) => route.name === 'SupportAdmin')) {
      routes.push({
        name: 'SupportAdmin',
        authScope: [{ level: PermissionLevel.All, scope: AuthScope.Operational }],
        displayName: 'Support',
        route: 'SupportAdmin',
        external: false,
        id: 'top_appbar_support',
      })
    }

    return [
      ...routes,
      {
        name: 'Timeline',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Timeline }],
        displayName: 'Timeline',
        route: `${baseUrl}/Reports`,
        external: true,
        id: 'top_appbar_timeline',
      },
      {
        name: 'Calendar',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Calendar }],
        displayName: 'Calendar',
        route: `${baseUrl}/Reports/CalendarReports`,
        external: true,
        id: 'top_appbar_calendar',
      },
      {
        name: 'Products',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Products }],
        displayName: 'Products',
        route: `${baseUrl}/Products`,
        external: true,
        id: 'top_appbar_products',
      },
      {
        name: 'Customers',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Customers }],
        displayName: 'Customers',
        route: `${baseUrl}/Customers`,
        external: true,
        id: 'top_appbar_customers',
      },
      {
        name: 'Contacts',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Contacts }],
        displayName: 'Contacts',
        route: `${baseUrl}/Contacts`,
        external: true,
        id: 'top_appbar_contacts',
      },
      {
        name: 'Forms',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Forms }],
        displayName: 'Forms',
        route: `${baseUrl}/Forms`,
        external: true,
        id: 'top_appbar_forms',
      },
      {
        name: 'Users',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Users }],
        displayName: 'Users',
        route: `${baseUrl}/Users/Users`,
        external: true,
        id: 'top_appbar_users',
      },
      {
        name: 'Reports',
        authScope: [{ level: PermissionLevel.Read, scope: AuthScope.Reports }],
        displayName: 'Reports',
        route: `${baseUrl}/Reports/Reports/Index/0`,
        external: true,
        id: 'top_appbar_reports',
      },
    ]

  })

  const menuItems = computed<ITopAppBarMenuItem[]>(() => {
    const items: ITopAppBarMenuItem[] = []
    ROUTES.value.forEach(route => {
      if (route.authScope.some(scope => authStore.authScope.some(tokenScope => authPermissionEquals(scope, tokenScope)))) {
        try {
          if (!route.external) {
            router.resolve({ name: route.name }) // check if internal link exists
          }
          items.push({
            displayName: route.displayName,
            route: route.route,
            external: route.external,
            id: route.id,
          })
        } catch {
          if (configStore.env !== 'production') {
            console.error(`Error in AppSkynamo 'TopAppBarItemStore': No route '${route.name}'.`)
          }
        }
      }
    })

    return items
  })

  function update() {
    const authStore = useAuthStore()

    if (!interval.value) {
      interval.value = setInterval(() => {
        dynamicRoutes.value = router.getRoutes()
      }, 1000)
    }

    authStore.$subscribe((_mutation, state) => {
      if (state.insightsUrl && state.insightsUrl.length > 0) {
        const baseUrl = state.insightsUrl

        userSettingsRoute.value =
          state.instanceName !== 'skynamo'
            ? `${baseUrl}/Users/Users/Edit/${state.userId}`
            : undefined

        if (state.authScope.some(permission => authPermissionEquals(permission, { level: PermissionLevel.Read, scope: AuthScope.Settings }))) {
          const route = state.authScope.some(permission => authPermissionEquals(permission, { level: PermissionLevel.Read, scope: AuthScope.Devices }))
            ? `${baseUrl}/MobileDevices`
            : `${baseUrl}/Warehouses`
          settingsMenuItem.value = {
            iconName: 'settings',
            route: route,
            external: true,
            id: 'top_appbar_settings',
          }
        }
      }
    })
  }

  function setToUpdatedVersion(value: boolean) {
    updatedVersion.value = value
  }

  return {
    updatedVersion,
    menuItems,
    settingsMenuItem,
    userSettingsRoute,
    update,
    setToUpdatedVersion,
  }
})

export default useTopAppBarItemsStore
